import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPlus, FiMinus } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const faqs = [
  {
    question: "How much does a website cost?",
    answer: "Every project is different, so pricing depends on the scope of work. We provide a personalised quote based on your business goals, requirements, and budget. Our solutions are designed to deliver excellent value while remaining cost-effective."
  },
  {
    question: "How long does it take to build a website?",
    answer: "Most standard business websites are completed within 2 working days after receiving all required content and project information. Larger or more complex projects may require additional time."
  },
  {
    question: "Do you redesign existing websites?",
    answer: "Yes. We can redesign, modernize, and improve existing websites while maintaining your brand identity and business goals."
  },
  {
    question: "Do I need to provide the content for my website?",
    answer: "Yes. If you already have your text, images, and branding, we'll use them. If not, we can also help create professional content for your website."
  },
  {
    question: "Do you provide website hosting?",
    answer: "Yes. We can provide reliable website hosting and help you choose the best hosting solution for your business."
  },
  {
    question: "Can you manage my website after launch?",
    answer: "Yes. We offer ongoing website maintenance, updates, and support to keep your website secure, up-to-date, and performing well."
  }
];

const FAQItem = ({ faq, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="mb-4"
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        className={`w-full flex items-center justify-between p-6 md:p-8 rounded-[2rem] text-left transition-all duration-300 ${
          isOpen 
            ? 'bg-emerald-600 text-white shadow-xl shadow-emerald-600/20' 
            : 'bg-slate-50 text-slate-900 hover:bg-slate-100'
        }`}
      >
        <span className="text-lg md:text-xl font-bold leading-tight">{faq.question}</span>
        <div className={`flex-shrink-0 ml-4 p-2 rounded-full ${isOpen ? 'bg-white/20' : 'bg-emerald-100 text-emerald-600'}`}>
          <SafeIcon icon={isOpen ? FiMinus : FiPlus} className="text-xl" />
        </div>
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="p-8 text-slate-600 text-lg leading-relaxed font-medium bg-white rounded-b-[2rem] -mt-4 border-x border-b border-slate-100">
              {faq.answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  return (
    <section id="faq" className="py-24 bg-white border-t border-slate-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-emerald-600 font-black text-sm uppercase tracking-[0.2em] mb-4">Common Questions</h2>
          <p className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">Frequently Asked Questions</p>
        </div>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItem key={index} faq={faq} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
